import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, BarChart3, BookOpen, Mail, Eye, Send, CreditCard, RefreshCw, Link, DollarSign, MessageSquare, Star, Check, X, FileText } from 'lucide-react';
import AnalyticsDashboard from '@/components/AnalyticsDashboard';
import BlogAdminPanel from '@/components/BlogAdminPanel';

const AdminPage = () => {
  const navigate = useNavigate();
  const [adminPassword, setAdminPassword] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [adminToken, setAdminToken] = useState('');
  const [devotionals, setDevotionals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editDialog, setEditDialog] = useState(false);
  const [currentDevotional, setCurrentDevotional] = useState(null);
  const [activeSection, setActiveSection] = useState('analytics'); // 'content', 'analytics', 'emails', 'payments', 'testimonials', or 'blog'
  
  // Email template state
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [editTemplateDialog, setEditTemplateDialog] = useState(false);
  const [currentTemplate, setCurrentTemplate] = useState(null);
  const [previewDialog, setPreviewDialog] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [testEmailDialog, setTestEmailDialog] = useState(false);
  const [testEmailAddress, setTestEmailAddress] = useState('');
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [templateFormData, setTemplateFormData] = useState({
    name: '',
    subject: '',
    greeting: '',
    body: '',
    closing: ''
  });
  
  // Payment state
  const [paymentStats, setPaymentStats] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [createPaymentDialog, setCreatePaymentDialog] = useState(false);
  const [refundDialog, setRefundDialog] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    description: '',
    customer_email: '',
    plan_type: 'custom'
  });
  const [refundAmount, setRefundAmount] = useState('');
  const [refundReason, setRefundReason] = useState('');
  const [userSearch, setUserSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  
  // Testimonial state
  const [testimonials, setTestimonials] = useState([]);
  const [testimonialCounts, setTestimonialCounts] = useState({ pending: 0, approved: 0, rejected: 0 });
  const [testimonialFilter, setTestimonialFilter] = useState('pending');
  
  const [formData, setFormData] = useState({
    day_number: '',
    date: '',
    liturgical_day: '',
    season: 'Ordinary Time',
    scripture: '',
    reflection: '',
    writing_prompt: '',
    prayer: ''
  });

  // Calculate day of year from date string
  const calculateDayOfYear = (dateString) => {
    if (!dateString) return '';
    
    const months = {
      'January': 1, 'February': 2, 'March': 3, 'April': 4, 'May': 5, 'June': 6,
      'July': 7, 'August': 8, 'September': 9, 'October': 10, 'November': 11, 'December': 12
    };
    
    // Parse "Month Day" format (e.g., "February 1")
    const parts = dateString.trim().split(' ');
    if (parts.length !== 2) return '';
    
    const monthName = parts[0];
    const day = parseInt(parts[1]);
    
    if (!months[monthName] || isNaN(day)) return '';
    
    const month = months[monthName];
    const year = 2025; // Use a non-leap year for consistency
    const date = new Date(year, month - 1, day);
    
    const start = new Date(year, 0, 1);
    const diff = date - start;
    const oneDay = 1000 * 60 * 60 * 24;
    const dayOfYear = Math.floor(diff / oneDay) + 1;
    
    return dayOfYear;
  };

  // Auto-calculate day number when date changes
  const handleDateChange = (newDate) => {
    setFormData(prev => ({
      ...prev,
      date: newDate,
      day_number: calculateDayOfYear(newDate) || prev.day_number
    }));
  };

  const handleAdminLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(
        `${API}/admin/login`,
        { password: adminPassword },
        { withCredentials: true }
      );
      setIsAuthenticated(true);
      setAdminToken(adminPassword); // Store token for Blog admin panel
      loadDevotionals();
      toast.success('Admin access granted');
    } catch (error) {
      toast.error('Invalid admin password');
    } finally {
      setLoading(false);
    }
  };

  const loadDevotionals = async () => {
    try {
      const response = await axios.get(`${API}/admin/devotionals`, { withCredentials: true });
      setDevotionals(response.data);
    } catch (error) {
      toast.error('Failed to load devotionals');
    }
  };

  const handleSubmit = async () => {
    setLoading(true);

    try {
      if (currentDevotional) {
        await axios.put(
          `${API}/admin/devotionals/${currentDevotional.devotional_id}`,
          formData,
          { withCredentials: true }
        );
        toast.success('Devotional updated');
      } else {
        await axios.post(
          `${API}/admin/devotionals`,
          formData,
          { withCredentials: true }
        );
        toast.success('Devotional created');
      }
      setEditDialog(false);
      loadDevotionals();
      resetForm();
    } catch (error) {
      toast.error('Failed to save devotional');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (devotionalId) => {
    if (!confirm('Are you sure you want to delete this devotional?')) return;

    try {
      await axios.delete(`${API}/admin/devotionals/${devotionalId}`, { withCredentials: true });
      toast.success('Devotional deleted');
      loadDevotionals();
    } catch (error) {
      toast.error('Failed to delete devotional');
    }
  };

  const resetForm = () => {
    setFormData({
      day_number: '',
      date: '',
      liturgical_day: '',
      season: 'Ordinary Time',
      scripture: '',
      reflection: '',
      writing_prompt: '',
      prayer: ''
    });
    setCurrentDevotional(null);
  };

  const openEditDialog = (devotional = null) => {
    if (devotional) {
      setCurrentDevotional(devotional);
      setFormData({
        day_number: devotional.day_number,
        date: devotional.date,
        liturgical_day: devotional.liturgical_day,
        season: devotional.season,
        scripture: devotional.scripture,
        reflection: devotional.reflection,
        writing_prompt: devotional.writing_prompt,
        prayer: devotional.prayer
      });
    } else {
      resetForm();
    }
    setEditDialog(true);
  };

  // Email Template Functions
  const loadEmailTemplates = async () => {
    try {
      const response = await axios.get(`${API}/admin/email-templates`, { withCredentials: true });
      setEmailTemplates(response.data.templates);
    } catch (error) {
      toast.error('Failed to load email templates');
    }
  };

  const loadSchedulerStatus = async () => {
    try {
      const response = await axios.get(`${API}/admin/scheduler-status`, { withCredentials: true });
      setSchedulerStatus(response.data);
    } catch (error) {
      console.error('Failed to load scheduler status');
    }
  };

  const openTemplateEditDialog = (template) => {
    setCurrentTemplate(template);
    setTemplateFormData({
      name: template.name,
      subject: template.subject,
      greeting: template.greeting,
      body: template.body,
      closing: template.closing
    });
    setEditTemplateDialog(true);
  };

  const handleTemplateSubmit = async () => {
    setLoading(true);
    try {
      await axios.put(
        `${API}/admin/email-templates/${currentTemplate.id}`,
        templateFormData,
        { withCredentials: true }
      );
      toast.success('Template updated successfully');
      setEditTemplateDialog(false);
      loadEmailTemplates();
    } catch (error) {
      toast.error('Failed to update template');
    } finally {
      setLoading(false);
    }
  };

  const handlePreviewTemplate = async (templateId) => {
    try {
      const response = await axios.post(
        `${API}/admin/preview-template`,
        { template_id: templateId, first_name: 'Sarah' },
        { withCredentials: true }
      );
      setPreviewHtml(response.data.html_preview);
      setPreviewDialog(true);
    } catch (error) {
      toast.error('Failed to preview template');
    }
  };

  const handleSendTestEmail = async () => {
    if (!testEmailAddress) {
      toast.error('Please enter an email address');
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/admin/test-email`,
        { to_email: testEmailAddress, template_id: currentTemplate?.id },
        { withCredentials: true }
      );
      toast.success(`Test email sent using "${response.data.template_used}" template`);
      setTestEmailDialog(false);
      setTestEmailAddress('');
    } catch (error) {
      toast.error('Failed to send test email');
    } finally {
      setLoading(false);
    }
  };

  const handleSendDailyReminders = async () => {
    if (!confirm('This will send reminder emails to ALL subscribed users with reminders enabled. Continue?')) return;
    
    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/admin/send-daily-reminders`,
        {},
        { withCredentials: true }
      );
      toast.success(`Sent ${response.data.sent} emails (${response.data.failed} failed)`);
    } catch (error) {
      toast.error('Failed to send daily reminders');
    } finally {
      setLoading(false);
    }
  };

  // Payment Functions
  const loadPaymentData = async () => {
    try {
      const [statsRes, txnRes] = await Promise.all([
        axios.get(`${API}/admin/payments/stats`, { withCredentials: true }),
        axios.get(`${API}/admin/payments/transactions?limit=20`, { withCredentials: true })
      ]);
      setPaymentStats(statsRes.data);
      setTransactions(txnRes.data.transactions);
    } catch (error) {
      toast.error('Failed to load payment data');
    }
  };

  const searchUsers = async (query) => {
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }
    try {
      const response = await axios.get(
        `${API}/admin/users/search?q=${encodeURIComponent(query)}`,
        { withCredentials: true }
      );
      setSearchResults(response.data.users);
    } catch (error) {
      console.error('User search failed');
    }
  };

  const handleCreatePaymentLink = async () => {
    if (!paymentForm.amount || parseFloat(paymentForm.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/admin/payments/create-link`,
        {
          amount: parseFloat(paymentForm.amount),
          description: paymentForm.description || 'Rooted Faith Journal',
          customer_email: paymentForm.customer_email || null,
          plan_type: paymentForm.plan_type,
          user_id: selectedUser?.user_id || null
        },
        { withCredentials: true }
      );
      
      // Copy link to clipboard
      navigator.clipboard.writeText(response.data.payment_link);
      toast.success('Payment link created and copied to clipboard!');
      
      setCreatePaymentDialog(false);
      setPaymentForm({ amount: '', description: '', customer_email: '', plan_type: 'custom' });
      setSelectedUser(null);
      loadPaymentData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create payment link');
    } finally {
      setLoading(false);
    }
  };

  const handleRefund = async () => {
    if (!selectedTransaction) return;
    
    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/admin/payments/refund`,
        {
          transaction_id: selectedTransaction.transaction_id,
          amount: refundAmount ? parseFloat(refundAmount) : null,
          reason: refundReason || null
        },
        { withCredentials: true }
      );
      
      toast.success(`Refund of $${response.data.refund_amount} processed successfully`);
      setRefundDialog(false);
      setSelectedTransaction(null);
      setRefundAmount('');
      setRefundReason('');
      loadPaymentData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process refund');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  // Testimonial Functions
  const loadTestimonials = async (filter = testimonialFilter) => {
    try {
      const response = await axios.get(
        `${API}/admin/testimonials${filter ? `?status=${filter}` : ''}`,
        { withCredentials: true }
      );
      setTestimonials(response.data.testimonials);
      setTestimonialCounts(response.data.counts);
    } catch (error) {
      toast.error('Failed to load testimonials');
    }
  };

  const handleTestimonialAction = async (testimonialId, status, featured = false) => {
    setLoading(true);
    try {
      await axios.put(
        `${API}/admin/testimonials/${testimonialId}`,
        { status, featured },
        { withCredentials: true }
      );
      toast.success(`Testimonial ${status}`);
      loadTestimonials();
    } catch (error) {
      toast.error('Failed to update testimonial');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTestimonial = async (testimonialId) => {
    if (!confirm('Are you sure you want to delete this testimonial?')) return;
    
    setLoading(true);
    try {
      await axios.delete(
        `${API}/admin/testimonials/${testimonialId}`,
        { withCredentials: true }
      );
      toast.success('Testimonial deleted');
      loadTestimonials();
    } catch (error) {
      toast.error('Failed to delete testimonial');
    } finally {
      setLoading(false);
    }
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        className={`w-4 h-4 ${i < rating ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300'}`}
      />
    ));
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <Card className="w-full max-w-md p-8 border border-primary/10">
          <h2 className="text-3xl mb-6 text-center" style={{ fontFamily: 'Playfair Display, serif' }}>
            Admin Access
          </h2>
          <form onSubmit={handleAdminLogin} className="space-y-4">
            <div>
              <Label htmlFor="password">Admin Password</Label>
              <Input
                id="password"
                data-testid="admin-password-input"
                type="password"
                value={adminPassword}
                onChange={(e) => setAdminPassword(e.target.value)}
                required
                className="mt-1"
              />
            </div>
            <Button 
              data-testid="admin-login-btn"
              type="submit" 
              className="w-full rounded-full" 
              disabled={loading}
            >
              {loading ? 'Verifying...' : 'Access Admin'}
            </Button>
          </form>
          <Button
            variant="ghost"
            className="w-full mt-4"
            onClick={() => navigate('/dashboard')}
            data-testid="back-btn"
          >
            Back to Dashboard
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <div className="max-w-6xl mx-auto px-6 py-12">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-4xl" style={{ fontFamily: 'Playfair Display, serif' }} data-testid="admin-title">
            Admin Dashboard
          </h1>
          <Button variant="outline" onClick={() => navigate('/dashboard')} data-testid="back-btn">
            Back to App
          </Button>
        </div>

        {/* Section Tabs */}
        <div className="flex gap-2 mb-8 border-b pb-4 flex-wrap">
          <Button
            variant={activeSection === 'analytics' ? 'default' : 'outline'}
            onClick={() => setActiveSection('analytics')}
            data-testid="analytics-tab-btn"
            className="flex items-center gap-2"
          >
            <BarChart3 className="w-4 h-4" strokeWidth={1.5} />
            Analytics
          </Button>
          <Button
            variant={activeSection === 'content' ? 'default' : 'outline'}
            onClick={() => setActiveSection('content')}
            data-testid="content-tab-btn"
            className="flex items-center gap-2"
          >
            <BookOpen className="w-4 h-4" strokeWidth={1.5} />
            Content
          </Button>
          <Button
            variant={activeSection === 'emails' ? 'default' : 'outline'}
            onClick={() => { setActiveSection('emails'); loadEmailTemplates(); loadSchedulerStatus(); }}
            data-testid="emails-tab-btn"
            className="flex items-center gap-2"
          >
            <Mail className="w-4 h-4" strokeWidth={1.5} />
            Emails
          </Button>
          <Button
            variant={activeSection === 'payments' ? 'default' : 'outline'}
            onClick={() => { setActiveSection('payments'); loadPaymentData(); }}
            data-testid="payments-tab-btn"
            className="flex items-center gap-2"
          >
            <CreditCard className="w-4 h-4" strokeWidth={1.5} />
            Payments
          </Button>
          <Button
            variant={activeSection === 'testimonials' ? 'default' : 'outline'}
            onClick={() => { setActiveSection('testimonials'); loadTestimonials(); }}
            data-testid="testimonials-tab-btn"
            className="flex items-center gap-2"
          >
            <MessageSquare className="w-4 h-4" strokeWidth={1.5} />
            Testimonials
            {testimonialCounts.pending > 0 && (
              <span className="ml-1 px-2 py-0.5 text-xs bg-orange-500 text-white rounded-full">
                {testimonialCounts.pending}
              </span>
            )}
          </Button>
          <Button
            variant={activeSection === 'blog' ? 'default' : 'outline'}
            onClick={() => setActiveSection('blog')}
            data-testid="blog-tab-btn"
            className="flex items-center gap-2"
          >
            <FileText className="w-4 h-4" strokeWidth={1.5} />
            Blog & SEO
          </Button>
        </div>

        {/* Analytics Section */}
        {activeSection === 'analytics' && (
          <AnalyticsDashboard />
        )}

        {/* Content Management Section */}
        {activeSection === 'content' && (
          <>
            <div className="flex items-center justify-between mb-6">
              <p className="text-muted-foreground">
                Total devotionals: {devotionals.length} / 365
              </p>
              <Button onClick={() => openEditDialog()} data-testid="add-devotional-btn">
                <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} />
                Add Devotional
              </Button>
            </div>

            <div className="grid gap-4" data-testid="devotionals-list">
              {devotionals.map((dev) => (
                <Card key={dev.devotional_id} className="p-6 border border-primary/10" data-testid={`admin-devotional-${dev.day_number}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-sm text-muted-foreground">Day {dev.day_number}</span>
                        <span className="text-xs px-2 py-1 rounded-full bg-primary/10 text-primary">
                          {dev.season}
                        </span>
                      </div>
                      <h3 className="text-xl mb-1" style={{ fontFamily: 'Playfair Display, serif' }}>
                        {dev.liturgical_day}
                      </h3>
                      <p className="text-sm text-muted-foreground">{dev.date}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openEditDialog(dev)}
                        data-testid="edit-devotional-btn"
                      >
                        <Edit className="w-4 h-4" strokeWidth={1.5} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(dev.devotional_id)}
                        data-testid="delete-devotional-btn"
                      >
                        <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </>
        )}

        {/* Email Templates Section */}
        {activeSection === 'emails' && (
          <div className="space-y-6">
            {/* Scheduler Status Card */}
            {schedulerStatus && (
              <Card className="p-6 bg-gradient-to-r from-primary/5 to-primary/10 border-primary/20" data-testid="scheduler-status-card">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`w-3 h-3 rounded-full ${schedulerStatus.scheduler_running ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`} />
                    <div>
                      <h3 className="font-medium">
                        Automatic Scheduler: {schedulerStatus.scheduler_running ? 'Active' : 'Inactive'}
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        Current time: {schedulerStatus.current_time_utc} UTC • 
                        Checking every minute for users whose reminder time matches
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-semibold">{schedulerStatus.reminders_sent_today}</p>
                    <p className="text-xs text-muted-foreground">Sent today</p>
                  </div>
                </div>
                
                {schedulerStatus.upcoming_times_today?.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-primary/10">
                    <p className="text-sm text-muted-foreground mb-2">Upcoming reminder times today (UTC):</p>
                    <div className="flex flex-wrap gap-2">
                      {schedulerStatus.upcoming_times_today.map((slot, idx) => (
                        <span key={idx} className="px-3 py-1 rounded-full bg-background text-sm">
                          {slot.time} ({slot.user_count} {slot.user_count === 1 ? 'user' : 'users'})
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                
                <div className="mt-4 flex gap-4 text-sm">
                  <span className="text-muted-foreground">
                    Total with reminders: <strong>{schedulerStatus.total_users_with_reminders}</strong>
                  </span>
                  <span className="text-muted-foreground">
                    Pending today: <strong>{schedulerStatus.reminders_pending_today}</strong>
                  </span>
                </div>
              </Card>
            )}

            {/* Header with actions */}
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl mb-1" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Email Reminder Templates
                </h2>
                <p className="text-muted-foreground text-sm">
                  These templates rotate randomly for daily reminder emails
                </p>
              </div>
              <div className="flex gap-2">
                <Button 
                  variant="outline"
                  onClick={() => { setCurrentTemplate(null); setTestEmailDialog(true); }}
                  data-testid="send-test-email-btn"
                >
                  <Send className="w-4 h-4 mr-2" strokeWidth={1.5} />
                  Send Test Email
                </Button>
                <Button 
                  onClick={handleSendDailyReminders}
                  disabled={loading}
                  data-testid="send-all-reminders-btn"
                >
                  <Mail className="w-4 h-4 mr-2" strokeWidth={1.5} />
                  Send Daily Reminders
                </Button>
              </div>
            </div>

            {/* Template Cards */}
            <div className="grid gap-4">
              {emailTemplates.map((template) => (
                <Card key={template.id} className="p-6 border border-primary/10" data-testid={`email-template-${template.id}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-xs px-2 py-1 rounded-full bg-primary/10 text-primary">
                          Template {template.id}
                        </span>
                        <h3 className="text-lg font-medium">{template.name}</h3>
                      </div>
                      <p className="text-sm text-muted-foreground mb-2">
                        <strong>Subject:</strong> {template.subject.replace('{first_name}', '[Name]')}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        <strong>Opening:</strong> {template.greeting.replace('{first_name}', '[Name]')}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePreviewTemplate(template.id)}
                        data-testid={`preview-template-${template.id}-btn`}
                      >
                        <Eye className="w-4 h-4 mr-1" strokeWidth={1.5} />
                        Preview
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openTemplateEditDialog(template)}
                        data-testid={`edit-template-${template.id}-btn`}
                      >
                        <Edit className="w-4 h-4 mr-1" strokeWidth={1.5} />
                        Edit
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => { setCurrentTemplate(template); setTestEmailDialog(true); }}
                        data-testid={`test-template-${template.id}-btn`}
                      >
                        <Send className="w-4 h-4 mr-1" strokeWidth={1.5} />
                        Test
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Payments Section */}
        {activeSection === 'payments' && (
          <div className="space-y-6">
            {/* Payment Stats */}
            {paymentStats && (
              <div className="grid grid-cols-4 gap-4">
                <Card className="p-6 text-center">
                  <DollarSign className="w-8 h-8 mx-auto mb-2 text-green-600" strokeWidth={1.5} />
                  <p className="text-3xl font-light" style={{ fontFamily: 'Playfair Display, serif' }}>
                    ${paymentStats.net_revenue.toLocaleString()}
                  </p>
                  <p className="text-sm text-muted-foreground">Net Revenue</p>
                </Card>
                <Card className="p-6 text-center">
                  <CreditCard className="w-8 h-8 mx-auto mb-2 text-primary" strokeWidth={1.5} />
                  <p className="text-3xl font-light" style={{ fontFamily: 'Playfair Display, serif' }}>
                    ${paymentStats.month_revenue.toLocaleString()}
                  </p>
                  <p className="text-sm text-muted-foreground">This Month</p>
                </Card>
                <Card className="p-6 text-center">
                  <p className="text-3xl font-light" style={{ fontFamily: 'Playfair Display, serif' }}>
                    {paymentStats.paid_transactions}
                  </p>
                  <p className="text-sm text-muted-foreground">Successful Payments</p>
                </Card>
                <Card className="p-6 text-center">
                  <RefreshCw className="w-8 h-8 mx-auto mb-2 text-orange-500" strokeWidth={1.5} />
                  <p className="text-3xl font-light" style={{ fontFamily: 'Playfair Display, serif' }}>
                    ${paymentStats.total_refunds.toLocaleString()}
                  </p>
                  <p className="text-sm text-muted-foreground">Total Refunds</p>
                </Card>
              </div>
            )}

            {/* Actions */}
            <div className="flex items-center justify-between">
              <h2 className="text-2xl" style={{ fontFamily: 'Playfair Display, serif' }}>
                Recent Transactions
              </h2>
              <Button onClick={() => setCreatePaymentDialog(true)} data-testid="create-payment-link-btn">
                <Link className="w-4 h-4 mr-2" strokeWidth={1.5} />
                Create Payment Link
              </Button>
            </div>

            {/* Transactions Table */}
            <Card className="overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium">Date</th>
                      <th className="px-4 py-3 text-left text-sm font-medium">Customer</th>
                      <th className="px-4 py-3 text-left text-sm font-medium">Description</th>
                      <th className="px-4 py-3 text-left text-sm font-medium">Amount</th>
                      <th className="px-4 py-3 text-left text-sm font-medium">Status</th>
                      <th className="px-4 py-3 text-left text-sm font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {transactions.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="px-4 py-8 text-center text-muted-foreground">
                          No transactions yet
                        </td>
                      </tr>
                    ) : (
                      transactions.map((txn) => (
                        <tr key={txn.transaction_id} className="hover:bg-muted/30">
                          <td className="px-4 py-3 text-sm">
                            {new Date(txn.created_at).toLocaleDateString()}
                          </td>
                          <td className="px-4 py-3">
                            <p className="text-sm font-medium">{txn.user_name || 'N/A'}</p>
                            <p className="text-xs text-muted-foreground">{txn.user_email || 'N/A'}</p>
                          </td>
                          <td className="px-4 py-3 text-sm">
                            {txn.description || txn.metadata?.plan_type || 'Subscription'}
                          </td>
                          <td className="px-4 py-3 text-sm font-medium">
                            ${txn.amount?.toFixed(2)}
                            {txn.refunded && (
                              <span className="ml-1 text-xs text-orange-600">
                                (Refunded: ${txn.refund_amount?.toFixed(2)})
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 rounded-full text-xs ${
                              txn.payment_status === 'paid' 
                                ? txn.refunded 
                                  ? 'bg-orange-100 text-orange-700' 
                                  : 'bg-green-100 text-green-700'
                                : 'bg-yellow-100 text-yellow-700'
                            }`}>
                              {txn.refunded ? 'Refunded' : txn.payment_status}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            {txn.payment_status === 'paid' && !txn.refunded && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => { setSelectedTransaction(txn); setRefundDialog(true); }}
                                data-testid={`refund-${txn.transaction_id}-btn`}
                              >
                                <RefreshCw className="w-3 h-3 mr-1" />
                                Refund
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>
        )}

        {/* Testimonials Section */}
        {activeSection === 'testimonials' && (
          <div className="space-y-6">
            {/* Stats */}
            <div className="grid grid-cols-3 gap-4">
              <Card 
                className={`p-6 text-center cursor-pointer transition ${testimonialFilter === 'pending' ? 'ring-2 ring-primary' : ''}`}
                onClick={() => { setTestimonialFilter('pending'); loadTestimonials('pending'); }}
              >
                <p className="text-3xl font-light text-orange-500" style={{ fontFamily: 'Playfair Display, serif' }}>
                  {testimonialCounts.pending}
                </p>
                <p className="text-sm text-muted-foreground">Pending Review</p>
              </Card>
              <Card 
                className={`p-6 text-center cursor-pointer transition ${testimonialFilter === 'approved' ? 'ring-2 ring-primary' : ''}`}
                onClick={() => { setTestimonialFilter('approved'); loadTestimonials('approved'); }}
              >
                <p className="text-3xl font-light text-green-500" style={{ fontFamily: 'Playfair Display, serif' }}>
                  {testimonialCounts.approved}
                </p>
                <p className="text-sm text-muted-foreground">Published</p>
              </Card>
              <Card 
                className={`p-6 text-center cursor-pointer transition ${testimonialFilter === 'rejected' ? 'ring-2 ring-primary' : ''}`}
                onClick={() => { setTestimonialFilter('rejected'); loadTestimonials('rejected'); }}
              >
                <p className="text-3xl font-light text-gray-400" style={{ fontFamily: 'Playfair Display, serif' }}>
                  {testimonialCounts.rejected}
                </p>
                <p className="text-sm text-muted-foreground">Rejected</p>
              </Card>
            </div>

            {/* Testimonials List */}
            <div className="space-y-4">
              <h2 className="text-2xl" style={{ fontFamily: 'Playfair Display, serif' }}>
                {testimonialFilter === 'pending' ? 'Pending Review' : testimonialFilter === 'approved' ? 'Published' : 'Rejected'} Testimonials
              </h2>
              
              {testimonials.length === 0 ? (
                <Card className="p-8 text-center text-muted-foreground">
                  No {testimonialFilter} testimonials
                </Card>
              ) : (
                testimonials.map((t) => (
                  <Card key={t.testimonial_id} className="p-6" data-testid={`testimonial-${t.testimonial_id}`}>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="font-medium">{t.name}</span>
                          {t.location && (
                            <span className="text-sm text-muted-foreground">• {t.location}</span>
                          )}
                          <div className="flex">{renderStars(t.rating)}</div>
                          {t.featured && (
                            <span className="px-2 py-0.5 text-xs bg-yellow-100 text-yellow-700 rounded">
                              Featured
                            </span>
                          )}
                        </div>
                        <p className="text-muted-foreground mb-2">"{t.content}"</p>
                        <div className="flex items-center gap-4 text-xs text-muted-foreground">
                          <span>Submitted: {new Date(t.created_at).toLocaleDateString()}</span>
                          {t.email && <span>Email: {t.email}</span>}
                        </div>
                      </div>
                      <div className="flex gap-2 ml-4">
                        {t.status === 'pending' && (
                          <>
                            <Button
                              size="sm"
                              onClick={() => handleTestimonialAction(t.testimonial_id, 'approved')}
                              disabled={loading}
                              className="bg-green-600 hover:bg-green-700"
                              data-testid={`approve-${t.testimonial_id}-btn`}
                            >
                              <Check className="w-4 h-4 mr-1" />
                              Approve
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleTestimonialAction(t.testimonial_id, 'rejected')}
                              disabled={loading}
                              data-testid={`reject-${t.testimonial_id}-btn`}
                            >
                              <X className="w-4 h-4 mr-1" />
                              Reject
                            </Button>
                          </>
                        )}
                        {t.status === 'approved' && (
                          <Button
                            size="sm"
                            variant={t.featured ? 'default' : 'outline'}
                            onClick={() => handleTestimonialAction(t.testimonial_id, 'approved', !t.featured)}
                            disabled={loading}
                          >
                            <Star className={`w-4 h-4 mr-1 ${t.featured ? 'fill-current' : ''}`} />
                            {t.featured ? 'Unfeature' : 'Feature'}
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDeleteTestimonial(t.testimonial_id)}
                          disabled={loading}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </Card>
                ))
              )}
            </div>
          </div>
        )}

        {/* Blog & SEO Section */}
        {activeSection === 'blog' && (
          <BlogAdminPanel adminToken={adminToken} />
        )}
      </div>

      {/* Edit/Create Dialog */}
      <Dialog open={editDialog} onOpenChange={setEditDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle data-testid="dialog-title">
              {currentDevotional ? 'Edit Devotional' : 'Create Devotional'}
            </DialogTitle>
            <DialogDescription>
              Fill in all fields for the devotional entry
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="date">Date</Label>
                <Input
                  id="date"
                  data-testid="date-input"
                  type="text"
                  placeholder="February 1"
                  value={formData.date}
                  onChange={(e) => handleDateChange(e.target.value)}
                  className="mt-1"
                />
                <p className="text-xs text-muted-foreground mt-1">Format: Month Day</p>
              </div>
              <div>
                <Label htmlFor="day_number">Day Number</Label>
                <Input
                  id="day_number"
                  data-testid="day-number-input"
                  type="number"
                  min="1"
                  max="365"
                  value={formData.day_number}
                  onChange={(e) => setFormData({ ...formData, day_number: parseInt(e.target.value) })}
                  className="mt-1"
                  readOnly
                />
                <p className="text-xs text-muted-foreground mt-1">Auto-calculated from date</p>
              </div>
            </div>

            <div>
              <Label htmlFor="liturgical_day">Liturgical Day</Label>
              <Input
                id="liturgical_day"
                data-testid="liturgical-day-input"
                type="text"
                placeholder="First Sunday of Advent"
                value={formData.liturgical_day}
                onChange={(e) => setFormData({ ...formData, liturgical_day: e.target.value })}
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="season">Season</Label>
              <Select
                value={formData.season}
                onValueChange={(value) => setFormData({ ...formData, season: value })}
              >
                <SelectTrigger className="mt-1" data-testid="season-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Advent">Advent</SelectItem>
                  <SelectItem value="Christmas">Christmas</SelectItem>
                  <SelectItem value="Epiphany">Epiphany</SelectItem>
                  <SelectItem value="Lent">Lent</SelectItem>
                  <SelectItem value="Holy Week">Holy Week</SelectItem>
                  <SelectItem value="Easter">Easter</SelectItem>
                  <SelectItem value="Ordinary Time">Ordinary Time</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="scripture">Scripture</Label>
              <Textarea
                id="scripture"
                data-testid="scripture-input"
                placeholder="Scripture passage..."
                value={formData.scripture}
                onChange={(e) => setFormData({ ...formData, scripture: e.target.value })}
                rows={4}
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="reflection">Reflection</Label>
              <Textarea
                id="reflection"
                data-testid="reflection-input"
                placeholder="Reflection text..."
                value={formData.reflection}
                onChange={(e) => setFormData({ ...formData, reflection: e.target.value })}
                rows={6}
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="writing_prompt">Writing Prompt</Label>
              <Textarea
                id="writing_prompt"
                data-testid="writing-prompt-input"
                placeholder="Writing prompt..."
                value={formData.writing_prompt}
                onChange={(e) => setFormData({ ...formData, writing_prompt: e.target.value })}
                rows={3}
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="prayer">Prayer</Label>
              <Textarea
                id="prayer"
                data-testid="prayer-input"
                placeholder="Prayer text..."
                value={formData.prayer}
                onChange={(e) => setFormData({ ...formData, prayer: e.target.value })}
                rows={4}
                className="mt-1"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog(false)} data-testid="cancel-btn">
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={loading} data-testid="save-devotional-btn">
              {loading ? 'Saving...' : 'Save Devotional'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Template Dialog */}
      <Dialog open={editTemplateDialog} onOpenChange={setEditTemplateDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Email Template</DialogTitle>
            <DialogDescription>
              Customize the email content. Use {'{first_name}'} to insert the recipient's name.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label htmlFor="template-name">Template Name</Label>
              <Input
                id="template-name"
                data-testid="template-name-input"
                value={templateFormData.name}
                onChange={(e) => setTemplateFormData({ ...templateFormData, name: e.target.value })}
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="template-subject">Subject Line</Label>
              <Input
                id="template-subject"
                data-testid="template-subject-input"
                placeholder="Hello, {first_name}"
                value={templateFormData.subject}
                onChange={(e) => setTemplateFormData({ ...templateFormData, subject: e.target.value })}
                className="mt-1"
              />
              <p className="text-xs text-muted-foreground mt-1">Use {'{first_name}'} to personalize</p>
            </div>

            <div>
              <Label htmlFor="template-greeting">Greeting</Label>
              <Input
                id="template-greeting"
                data-testid="template-greeting-input"
                placeholder="Dear {first_name},"
                value={templateFormData.greeting}
                onChange={(e) => setTemplateFormData({ ...templateFormData, greeting: e.target.value })}
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="template-body">Message Body</Label>
              <Textarea
                id="template-body"
                data-testid="template-body-input"
                placeholder="Enter the main message..."
                value={templateFormData.body}
                onChange={(e) => setTemplateFormData({ ...templateFormData, body: e.target.value })}
                rows={8}
                className="mt-1"
              />
              <p className="text-xs text-muted-foreground mt-1">Separate paragraphs with blank lines</p>
            </div>

            <div>
              <Label htmlFor="template-closing">Closing Line</Label>
              <Input
                id="template-closing"
                data-testid="template-closing-input"
                placeholder="With peace,"
                value={templateFormData.closing}
                onChange={(e) => setTemplateFormData({ ...templateFormData, closing: e.target.value })}
                className="mt-1"
              />
              <p className="text-xs text-muted-foreground mt-1">Leave empty if none needed (The Rooted Team 🌿 is added automatically)</p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditTemplateDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleTemplateSubmit} disabled={loading}>
              {loading ? 'Saving...' : 'Save Template'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Template Dialog */}
      <Dialog open={previewDialog} onOpenChange={setPreviewDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Email Preview</DialogTitle>
            <DialogDescription>
              This is how the email will appear to recipients
            </DialogDescription>
          </DialogHeader>
          
          <div 
            className="border rounded-lg p-4 bg-gray-50"
            dangerouslySetInnerHTML={{ __html: previewHtml }}
          />

          <DialogFooter>
            <Button variant="outline" onClick={() => setPreviewDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Test Email Dialog */}
      <Dialog open={testEmailDialog} onOpenChange={setTestEmailDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send Test Email</DialogTitle>
            <DialogDescription>
              {currentTemplate 
                ? `Send a test email using the "${currentTemplate.name}" template`
                : 'Send a test email with a random template'
              }
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label htmlFor="test-email">Email Address</Label>
              <Input
                id="test-email"
                data-testid="test-email-input"
                type="email"
                placeholder="your@email.com"
                value={testEmailAddress}
                onChange={(e) => setTestEmailAddress(e.target.value)}
                className="mt-1"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Enter any email address you have access to (your personal email works fine)
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setTestEmailDialog(false); setTestEmailAddress(''); }}>
              Cancel
            </Button>
            <Button onClick={handleSendTestEmail} disabled={loading}>
              {loading ? 'Sending...' : 'Send Test'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Payment Link Dialog */}
      <Dialog open={createPaymentDialog} onOpenChange={setCreatePaymentDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create Payment Link</DialogTitle>
            <DialogDescription>
              Generate a payment link to send to a customer
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label htmlFor="payment-amount">Amount ($)</Label>
              <Input
                id="payment-amount"
                data-testid="payment-amount-input"
                type="number"
                step="0.01"
                min="0.50"
                placeholder="49.99"
                value={paymentForm.amount}
                onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="payment-description">Description</Label>
              <Input
                id="payment-description"
                data-testid="payment-description-input"
                placeholder="Annual Subscription, Custom Plan, etc."
                value={paymentForm.description}
                onChange={(e) => setPaymentForm({ ...paymentForm, description: e.target.value })}
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="payment-plan-type">Plan Type</Label>
              <Select 
                value={paymentForm.plan_type} 
                onValueChange={(value) => setPaymentForm({ ...paymentForm, plan_type: value })}
              >
                <SelectTrigger className="mt-1" data-testid="payment-plan-type-select">
                  <SelectValue placeholder="Select plan type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="annual">Annual ($49.99)</SelectItem>
                  <SelectItem value="monthly">Monthly ($4.99)</SelectItem>
                  <SelectItem value="custom">Custom Amount</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="customer-email">Customer Email (Optional)</Label>
              <Input
                id="customer-email"
                data-testid="customer-email-input"
                type="email"
                placeholder="customer@email.com"
                value={paymentForm.customer_email}
                onChange={(e) => setPaymentForm({ ...paymentForm, customer_email: e.target.value })}
                className="mt-1"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Pre-fills the email field on checkout
              </p>
            </div>

            <div>
              <Label>Link to User (Optional)</Label>
              <Input
                placeholder="Search by email or name..."
                value={userSearch}
                onChange={(e) => {
                  setUserSearch(e.target.value);
                  searchUsers(e.target.value);
                }}
                className="mt-1"
                data-testid="user-search-input"
              />
              {searchResults.length > 0 && (
                <div className="mt-2 border rounded-md max-h-32 overflow-y-auto">
                  {searchResults.map((user) => (
                    <button
                      key={user.user_id}
                      className="w-full px-3 py-2 text-left hover:bg-muted text-sm flex items-center justify-between"
                      onClick={() => {
                        setSelectedUser(user);
                        setUserSearch('');
                        setSearchResults([]);
                        setPaymentForm({ ...paymentForm, customer_email: user.email });
                      }}
                    >
                      <span>{user.name} ({user.email})</span>
                      {user.is_subscribed && (
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">
                          Subscribed
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              )}
              {selectedUser && (
                <div className="mt-2 p-2 bg-muted rounded-md text-sm flex items-center justify-between">
                  <span>Linked to: {selectedUser.name}</span>
                  <Button variant="ghost" size="sm" onClick={() => setSelectedUser(null)}>
                    Remove
                  </Button>
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setCreatePaymentDialog(false);
              setPaymentForm({ amount: '', description: '', customer_email: '', plan_type: 'custom' });
              setSelectedUser(null);
              setUserSearch('');
            }}>
              Cancel
            </Button>
            <Button onClick={handleCreatePaymentLink} disabled={loading}>
              {loading ? 'Creating...' : 'Create & Copy Link'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Refund Dialog */}
      <Dialog open={refundDialog} onOpenChange={setRefundDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Issue Refund</DialogTitle>
            <DialogDescription>
              Process a refund for transaction {selectedTransaction?.transaction_id}
            </DialogDescription>
          </DialogHeader>

          {selectedTransaction && (
            <div className="space-y-4">
              <div className="p-4 bg-muted rounded-lg">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <span className="text-muted-foreground">Original Amount:</span>
                  <span className="font-medium">${selectedTransaction.amount?.toFixed(2)}</span>
                  <span className="text-muted-foreground">Customer:</span>
                  <span className="font-medium">{selectedTransaction.user_email || 'N/A'}</span>
                  <span className="text-muted-foreground">Date:</span>
                  <span className="font-medium">
                    {new Date(selectedTransaction.created_at).toLocaleDateString()}
                  </span>
                </div>
              </div>

              <div>
                <Label htmlFor="refund-amount">Refund Amount (leave blank for full refund)</Label>
                <Input
                  id="refund-amount"
                  data-testid="refund-amount-input"
                  type="number"
                  step="0.01"
                  min="0.01"
                  max={selectedTransaction.amount}
                  placeholder={`Full refund: $${selectedTransaction.amount?.toFixed(2)}`}
                  value={refundAmount}
                  onChange={(e) => setRefundAmount(e.target.value)}
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="refund-reason">Reason (Optional)</Label>
                <Input
                  id="refund-reason"
                  data-testid="refund-reason-input"
                  placeholder="Customer requested cancellation, etc."
                  value={refundReason}
                  onChange={(e) => setRefundReason(e.target.value)}
                  className="mt-1"
                />
              </div>

              <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg text-sm text-orange-800">
                <strong>Warning:</strong> Refunds cannot be undone. The customer's subscription will be cancelled if this is a full refund.
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setRefundDialog(false);
              setSelectedTransaction(null);
              setRefundAmount('');
              setRefundReason('');
            }}>
              Cancel
            </Button>
            <Button 
              variant="destructive"
              onClick={handleRefund} 
              disabled={loading}
            >
              {loading ? 'Processing...' : 'Process Refund'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPage;